import type { Database, Json } from '../../../types/database.types'

export type BrandPreset = Database['public']['Tables']['brand_presets']['Row']

// ---------------------------------------------------------------------------
// Layout axes (board 3c): purpose first, structure and density refine it.
// Values are stored in brand_presets.layout / report_data.layout_override —
// renaming any of them is a data migration, not a refactor. `section_order`
// (010) rides on the preset's layout only: a report keeps its own order in
// report_data.section_order, never inside layout_override.
// ---------------------------------------------------------------------------
export type ReportPurpose = 'full_assessment' | 'summary' | 'client_brief'
export type CoverStyle = 'logo_panel' | 'photo' | 'plain'
export type BodyColumns = 'single' | 'two'
export type LayoutDensity = 'compact' | 'standard' | 'spacious'
export type PhotoPlacement = 'inline' | 'plate' | 'appendix'
export type LogoSize = 'small' | 'medium' | 'large'
/** Company name beside a logo. 'auto' keeps each cover's own default:
    shown on the plain title page, hidden on the logo panel / photo covers.
    With no logo the name always stands in for it, whatever this says. */
export type OrgNameDisplay = 'auto' | 'show' | 'hide'

export interface BrandLayout {
  purpose: ReportPurpose
  cover: CoverStyle
  columns: BodyColumns
  density: LayoutDensity
  photos: PhotoPlacement
  logo_size: LogoSize
  org_name: OrgNameDisplay
  /** 010: the preset's full-report section order, as stored (unresolved —
      see effectiveSectionOrder). Null follows the built-in default. */
  section_order: string[] | null
}

export const DEFAULT_LAYOUT: BrandLayout = {
  purpose: 'full_assessment',
  cover: 'logo_panel',
  columns: 'single',
  density: 'standard',
  photos: 'inline',
  logo_size: 'medium',
  org_name: 'auto',
  section_order: null,
}

const pick = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')

/** Tolerant parse of a stored layout blob — unknown/missing axes fall back per-axis. */
export function parseBrandLayout(raw: Json | null | undefined): BrandLayout {
  const obj = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  const source = obj as Record<string, unknown>
  return {
    purpose: pick(
      source.purpose,
      ['full_assessment', 'summary', 'client_brief'],
      DEFAULT_LAYOUT.purpose
    ),
    cover: pick(source.cover, ['logo_panel', 'photo', 'plain'], DEFAULT_LAYOUT.cover),
    columns: pick(source.columns, ['single', 'two'], DEFAULT_LAYOUT.columns),
    density: pick(source.density, ['compact', 'standard', 'spacious'], DEFAULT_LAYOUT.density),
    photos: pick(source.photos, ['inline', 'plate', 'appendix'], DEFAULT_LAYOUT.photos),
    logo_size: pick(source.logo_size, ['small', 'medium', 'large'], DEFAULT_LAYOUT.logo_size),
    org_name: pick(source.org_name, ['auto', 'show', 'hide'], DEFAULT_LAYOUT.org_name),
    section_order: isStringArray(source.section_order) ? source.section_order : null,
  }
}

/** Whether the company name prints beneath the logo on this cover. */
export const showsOrgNameWithLogo = (layout: Pick<BrandLayout, 'cover' | 'org_name'>): boolean =>
  layout.org_name === 'auto' ? layout.cover === 'plain' : layout.org_name === 'show'

export interface LayoutOption<T extends string> {
  value: T
  label: string
  description?: string
}

export const PURPOSE_OPTIONS: LayoutOption<ReportPurpose>[] = [
  {
    value: 'full_assessment',
    label: 'Full assessment',
    description: 'Every section, methodology and appendices.',
  },
  {
    value: 'summary',
    label: 'Summary',
    description: 'Findings, tree table and actions. Method as an appendix.',
  },
  {
    value: 'client_brief',
    label: 'Client brief',
    description: 'Plain language, photo-led, no technical appendices.',
  },
]

export const COVER_OPTIONS: LayoutOption<CoverStyle>[] = [
  { value: 'logo_panel', label: 'Logo panel' },
  { value: 'photo', label: 'Full-bleed photo' },
  { value: 'plain', label: 'Plain title page' },
]

export const COLUMN_OPTIONS: LayoutOption<BodyColumns>[] = [
  { value: 'single', label: 'Single' },
  { value: 'two', label: 'Two column' },
]

export const DENSITY_OPTIONS: LayoutOption<LayoutDensity>[] = [
  { value: 'compact', label: 'Compact' },
  { value: 'standard', label: 'Standard' },
  { value: 'spacious', label: 'Spacious' },
]

export const LOGO_SIZE_OPTIONS: LayoutOption<LogoSize>[] = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
]

export const PHOTO_OPTIONS: LayoutOption<PhotoPlacement>[] = [
  { value: 'inline', label: 'Inline with the tree' },
  { value: 'plate', label: 'Grouped plate' },
  { value: 'appendix', label: 'Appendix' },
]

export const layoutSummary = (layout: BrandLayout): string => {
  const purpose = PURPOSE_OPTIONS.find((o) => o.value === layout.purpose)?.label ?? layout.purpose
  return `${purpose} · ${layout.density}`
}

// ---------------------------------------------------------------------------
// Embeddable typefaces. All eight are OFL-licensed and bundled as TTFs under
// frontend/public/fonts/ (see pdfFontService). No user font uploads.
// ---------------------------------------------------------------------------
export interface FontFace {
  family: string
  /** File name stem under /fonts/, e.g. 'lexend-deca' → lexend-deca-400.ttf */
  stem: string
  category: 'sans' | 'serif'
  /** Families without an italic cut fall back to upright in the PDF */
  hasItalic: boolean
}

export const FONT_FACES: FontFace[] = [
  { family: 'Lexend Deca', stem: 'lexend-deca', category: 'sans', hasItalic: false },
  { family: 'Source Serif 4', stem: 'source-serif-4', category: 'serif', hasItalic: true },
  { family: 'IBM Plex Sans', stem: 'ibm-plex-sans', category: 'sans', hasItalic: true },
  { family: 'Space Grotesk', stem: 'space-grotesk', category: 'sans', hasItalic: false },
  { family: 'Libre Baskerville', stem: 'libre-baskerville', category: 'serif', hasItalic: true },
  { family: 'Bitter', stem: 'bitter', category: 'serif', hasItalic: true },
  { family: 'Source Sans 3', stem: 'source-sans-3', category: 'sans', hasItalic: true },
  {
    family: 'Bricolage Grotesque',
    stem: 'bricolage-grotesque',
    category: 'sans',
    hasItalic: false,
  },
]

export const isEmbeddableFont = (family: string): boolean =>
  FONT_FACES.some((f) => f.family === family)

// ---------------------------------------------------------------------------
// Generation-time snapshot, stored in reports.metadata.brand. Immutable once
// written — this is what the version stamp and the JobReports brand line read,
// so it must survive preset edits and deletion.
// ---------------------------------------------------------------------------
export interface BrandSnapshot {
  preset_id: string
  preset_name: string
  preset_version: number
  primary_color: string
  accent_color: string
  heading_font: string
  body_font: string
  layout: BrandLayout
}

/** Style a snapshot borrows for any field it lacks */
type BrandStyle = Pick<
  BrandSnapshot,
  'primary_color' | 'accent_color' | 'heading_font' | 'body_font'
>

/** Last resort when the team has no default preset: the Organic system's own
    terracotta, sage and Lexend Deca (not the pre-redesign green and Source Sans 3) */
const SYSTEM_BRAND_STYLE: BrandStyle = {
  primary_color: '#b0602c',
  accent_color: '#7a8a5e',
  heading_font: 'Lexend Deca',
  body_font: 'Lexend Deca',
}

/**
 * Missing style fields fall back to the team's default preset (pass it as
 * `teamDefault`), then to the system style — never to hard-coded legacy values.
 */
export function parseBrandSnapshot(
  metadata: Json | null | undefined,
  teamDefault?: BrandStyle | null
): BrandSnapshot | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null
  const brand = (metadata as Record<string, unknown>).brand
  if (!brand || typeof brand !== 'object' || Array.isArray(brand)) return null
  const b = brand as Record<string, unknown>
  if (typeof b.preset_name !== 'string' || typeof b.preset_version !== 'number') return null
  const fallback = teamDefault ?? SYSTEM_BRAND_STYLE
  const str = (value: unknown, otherwise: string): string =>
    typeof value === 'string' ? value : otherwise
  return {
    preset_id: typeof b.preset_id === 'string' ? b.preset_id : '',
    preset_name: b.preset_name,
    preset_version: b.preset_version,
    primary_color: str(b.primary_color, fallback.primary_color),
    accent_color: str(b.accent_color, fallback.accent_color),
    heading_font: str(b.heading_font, fallback.heading_font),
    body_font: str(b.body_font, fallback.body_font),
    layout: parseBrandLayout((b.layout ?? null) as Json | null),
  }
}
